import type { ComponentProps } from 'react';
import React, { useLayoutEffect } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import {
  NOTICE_BANNER_PADDING,
  NoticeBannersStack,
  theme,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sweetgreen/garnish';

import { useActiveScreenOptions } from '@order/hooks';

import { DESKTOP_HEADER_HEIGHT } from '../Header/constants';

// ────────────────────────────────────────────────────────────────────────────────

export const NoticeBannersOverlay = () => {
  const { currentBreakpoint } = useResponsive();
  const safeAreaInsets = useSafeAreaInsets();
  const { clear } = useNoticeBannersStackContext();

  const activeScreenOptions = useActiveScreenOptions();
  const isFullScreenModal = checkIfFullScreenModal(activeScreenOptions);

  // ─────────────────────────────────────────────────────────────────

  const isPresentedFromBottom = currentBreakpoint.isMobile;
  const topOffset = isFullScreenModal ? 0 : DESKTOP_HEADER_HEIGHT;
  const offsetsStyle = useStyle(
    () => (isPresentedFromBottom ? { bottom: 0 } : { top: topOffset }),
    [isPresentedFromBottom, topOffset],
  );

  const bannerPadding: ComponentProps<
    typeof NoticeBannersStack
  >['bannerPadding'] = isPresentedFromBottom
    ? {
        ...NOTICE_BANNER_PADDING,
        bottom: Math.max(
          NOTICE_BANNER_PADDING.bottom,
          safeAreaInsets.bottom + theme.spacing.small,
        ),
      }
    : {
        ...NOTICE_BANNER_PADDING,
        top: Math.max(
          NOTICE_BANNER_PADDING.top,
          safeAreaInsets.top + theme.spacing.small,
        ),
      };

  // clear notice banners stack on screen type change
  useLayoutEffect(() => clear, [clear, isFullScreenModal]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <NoticeBannersStack
      fullWindowOverlay
      bannerPadding={bannerPadding}
      style={[styles.stack, StyleSheet.absoluteFill, offsetsStyle]}
      pointerEvents="box-none"
    />
  );
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

const checkIfFullScreenModal = (
  screenOptions: ReturnType<typeof useActiveScreenOptions>,
) => {
  const { presentation, headerShown } = screenOptions ?? {};

  return (
    (presentation === 'transparentModal' ||
      presentation === 'fullScreenModal') &&
    !headerShown
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stack: {
    zIndex: theme.zIndex.fixed,
  },
});
